import { useState, useRef, useEffect } from 'react'
import ReactMarkdown from 'react-markdown'
import { useNavigate } from 'react-router-dom'
import type { ActionItem } from '../../types/ActionItem'
import '../../style/views/ConversationView.css'

type Conversation = {
  id: string
  title?: string
  date_created?: string
  summary?: string
  action_items?: ActionItem[]
  dates?: Array<{ title?: string; datetime_start?: string }>
  emails?: Array<{ subject?: string; body?: string }>
  clients?: Array<{ first_name?: string; last_name?: string; email?: string }>
}

const stripHtmlToText = (value?: string) => {
  if (!value) return ''
  const withBreaks = value
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/div>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/h\d>/gi, '\n')
  if (typeof window !== 'undefined' && 'DOMParser' in window) {
    const doc = new DOMParser().parseFromString(withBreaks, 'text/html')
    return (doc.body.textContent || '').replace(/\u00a0/g, ' ').replace(/\r\n?/g, '\n')
  }
  return withBreaks
    .replace(/<[^>]*>/g, ' ')
    .replace(/\s+\n/g, '\n')
    .replace(/\u00a0/g, ' ')
    .replace(/\r\n?/g, '\n')
}

type PostMeetingViewProps = {
  activeEvent: {
    title: string
  } | null | undefined
  latestConversation: Conversation | null
}

const PostMeetingView = ({ activeEvent, latestConversation }: PostMeetingViewProps) => {
  const navigate = useNavigate()
  const [copiedKey, setCopiedKey] = useState<string | null>(null)
  const copyTimerRef = useRef<number | null>(null)

  useEffect(() => {
    return () => {
      if (copyTimerRef.current) {
        window.clearTimeout(copyTimerRef.current)
      }
    }
  }, [])

  const handleCopy = async (value: string, key?: string) => {
    if (!value) return
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value)
        if (key) {
          setCopiedKey(key)
          if (copyTimerRef.current) window.clearTimeout(copyTimerRef.current)
          copyTimerRef.current = window.setTimeout(() => setCopiedKey(null), 2000)
        }
        return
      }
    } catch (err) {
      console.error('Clipboard write failed', err)
    }
  }

  const hasActionItems = (latestConversation?.action_items?.length ?? 0) > 0
  const hasDates = (latestConversation?.dates?.length ?? 0) > 0
  const hasEmail = (latestConversation?.emails?.length ?? 0) > 0
  const emailDraft = latestConversation?.emails?.[0]
  
  const clientEmails = latestConversation?.clients
    ? latestConversation.clients.flatMap(c => c.email ? [c.email] : []).join(', ')
    : ''

  const bodyText = stripHtmlToText(emailDraft?.body)

  return (
    <>
      <div className="hero-card-adaptive post-meeting">
        <div className="hero-content">
          <h1 className="hero-title">Great call! ✓</h1>
          <p className="hero-subtitle">
            Bev captured your briefing and identified next steps for {activeEvent?.title || latestConversation?.title || 'your latest call'}.
          </p>
          <div className="hero-actions">
            {hasEmail && (
              <button type="button" 
                className="hero-btn primary"
                onClick={() => latestConversation && navigate(`/conversation/${latestConversation.id}`)}
              >
                Review Email
              </button>
            )}
            <button type="button" 
              className={`hero-btn ${hasEmail ? 'secondary' : 'primary'}`}
              onClick={() => latestConversation && navigate(`/conversation/${latestConversation.id}`)}
            >
              View Notes
            </button>
          </div>
        </div>
      </div>

      <div className="home-section">
        <p className="eyebrow">What Bev Captured</p>
        <div className="home-grid-adaptive">
          <div className="home-panel" style={{ gridColumn: 'span 2' }}>
            <h2 className="home-highlight-title" style={{ marginBottom: 16 }}>📝 Call Summary</h2>
            <div className="home-action-item" style={{ cursor: 'default', display: 'block' }}>
              <div className="assistant-markdown" style={{ fontSize: '15px', lineHeight: '1.6', color: 'var(--ink-strong)' }}>
                {latestConversation?.summary ? (
                  <ReactMarkdown>{latestConversation.summary}</ReactMarkdown>
                ) : (
                  `Summary generated from ${activeEvent?.title || latestConversation?.title || 'your meeting'}. Key decisions and project scope were identified.`
                )}
              </div>
            </div>
          </div>

          {hasDates && (
            <div className="home-panel">
              <h2 className="home-highlight-title" style={{ marginBottom: 16 }}>📅 Meetings Created</h2>
              <div className="home-action-items-list" style={{ gridTemplateColumns: '1fr' }}>
                {latestConversation?.dates?.map((date, pos) => (
                  <div key={`meeting-${pos}`} className="home-action-item" style={{ cursor: 'default' }}>
                    <div className="home-action-item-text"><strong>{date.title}</strong></div>
                    <span className="adaptive-item-badge done">Scheduled</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {hasActionItems && (
            <div className="home-panel">
              <h2 className="home-highlight-title" style={{ marginBottom: 16 }}>✏️ Action Items</h2>
              <div className="home-action-items-list" style={{ gridTemplateColumns: '1fr' }}>
                {latestConversation?.action_items?.map((item, idx) => (
                  <div key={item.id || idx} className="home-action-item">
                    <div className="home-action-item-text"><strong>{item.item}</strong></div>
                    <span className="due-date-pill">Action</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {hasEmail && (
        <div className="home-section">
          <p className="eyebrow">Follow-Up Draft</p>
          <div className="home-panel">
            <h2 className="home-highlight-title" style={{ marginBottom: 16 }}>📧 Drafted Follow-Up Email</h2>
            
            <div className="email-draft" style={{ background: 'transparent', border: 'none', padding: 0 }}>
              <div className="copy-field" style={{ marginBottom: 12 }}>
                <span className="copy-field-label">Contact email</span>
                <div className={`copy-box ${clientEmails ? 'has-copy' : 'muted'}`} style={{ paddingBottom: clientEmails ? 28 : 10 }}>
                  {latestConversation?.clients && latestConversation.clients.some(c => c.email) ? (
                    <div className="draft-email-cards in-copy-box">
                      {latestConversation.clients.flatMap((client, pos) => {
                      if (!client.email) return []
                        const email = client.email || ''
                        const name = [client.first_name, client.last_name].filter(Boolean).join(' ') || email
                        return [(
                          <button
                            type="button"
                            key={`client-email-${pos}`}
                            className="draft-email-card"
                            onClick={() => handleCopy(email, `client-${pos}`)}
                            title="Copy email"
                          >
                            <span className="draft-email-card-name">{name}</span>
                            <span className="draft-email-card-email">{email}</span>
                          </button>
                        )]
                      })}
                    </div>
                  ) : (
                    'No linked contact emails.'
                  )}
                  {clientEmails && (
                    <button
                      type="button"
                      className={`copy-chip ${copiedKey === 'client' ? 'copied' : ''}`}
                      onClick={() => handleCopy(clientEmails, 'client')}
                    >
                      {copiedKey === 'client' ? '✓' : 'Copy'}
                    </button>
                  )}
                </div>
              </div>
              
              <div className="copy-field" style={{ marginBottom: 12 }}>
                <span className="copy-field-label">Subject</span>
                <div className={`copy-box ${emailDraft?.subject ? 'has-copy' : 'muted'}`} style={{ paddingBottom: emailDraft?.subject ? 28 : 10 }}>
                  {emailDraft?.subject || 'No subject.'}
                  {emailDraft?.subject && (
                    <button
                      className={`copy-chip ${copiedKey === 'subject' ? 'copied' : ''}`}
                      onClick={() => handleCopy(emailDraft.subject!, 'subject')}
                    >
                      {copiedKey === 'subject' ? '✓' : 'Copy'}
                    </button>
                  )}
                </div>
              </div>

              <div className="copy-field">
                <span className="copy-field-label">Email body</span>
                <div className={`copy-box ${bodyText ? 'has-copy' : 'muted'}`} style={{ paddingBottom: bodyText ? 28 : 10, maxHeight: '240px', overflow: 'auto' }}>
                  {bodyText || 'No email body.'}
                  {bodyText && (
                    <button
                      type="button"
                      className={`copy-chip ${copiedKey === 'body' ? 'copied' : ''}`}
                      onClick={() => handleCopy(bodyText, 'body')}
                    >
                      {copiedKey === 'body' ? '✓' : 'Copy'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

export default PostMeetingView
